/**
 * The transport-agnostic chat state machine.
 *
 * This is the only place that knows AG-UI event semantics; components see
 * `ChatMessage[]` and nothing else. Swapping a transport changes what arrives,
 * never how it is folded.
 */
import { computed, readonly, ref, type Ref } from 'vue';

import { conversationsApi } from '../api/conversations';
import {
  EventType,
  INTERRUPT_REASON_TOOL_EXECUTION,
  isCancelledRun,
  runInterrupts,
  type ChatEvent,
} from '../types/events';
import type { ChatMessage, ContentPart, ReasoningStep } from '../types/message';
import { assertTransportHonoursCapabilities, type ChatTransport } from '../types/transport';
import { toUserMessage } from '@/lib/api/errors';

export type ChatStatus = 'idle' | 'streaming' | 'awaiting_input' | 'error';

export interface UseChatOptions {
  transport: ChatTransport;
  conversationId?: Ref<string | undefined>;
}

function newId(): string {
  return crypto.randomUUID?.() ?? `msg-${Date.now()}-${Math.random().toString(36).slice(2, 9)}`;
}

/** Appends text to the message's trailing text part, or starts one. */
function appendText(message: ChatMessage, delta: string): void {
  const last = message.content[message.content.length - 1];
  if (last?.type === 'text') {
    last.text += delta;
    return;
  }
  message.content.push({ type: 'text', text: delta });
}

function upsertStep(message: ChatMessage, step: ReasoningStep): ReasoningStep {
  message.steps ??= [];
  const existing = message.steps.find((s) => s.id === step.id);
  if (existing) return existing;
  message.steps.push(step);
  return step;
}

export function useChat(opts: UseChatOptions) {
  // Fail at wire-up rather than stranding a user at a live approval prompt that
  // no button can resolve.
  assertTransportHonoursCapabilities(opts.transport);

  const messages = ref<ChatMessage[]>([]);
  const status = ref<ChatStatus>('idle');
  const error = ref<string | null>(null);
  const contextId = ref<string | undefined>(undefined);
  const taskId = ref<string | undefined>(undefined);

  let controller: AbortController | null = null;

  /**
   * History is best-effort by design: a lost history row is strictly preferable
   * to a failed chat turn, so this never rejects and is never awaited on the
   * send path.
   */
  function persist(message: ChatMessage): void {
    const id = opts.conversationId?.value;
    if (!id) return;
    void conversationsApi.appendMessage(id, message).catch((err) => {
      console.warn('[chat] failed to persist message to history', err);
    });
  }

  /** Records the A2A session on the conversation so a reload can resume it. */
  function bindContext(threadId: string): void {
    if (!threadId || contextId.value === threadId) return;
    contextId.value = threadId;
    const id = opts.conversationId?.value;
    if (!id) return;
    void conversationsApi.update(id, { context_id: threadId }).catch((err) => {
      // Set-once: a second bind attempt is rejected by design, and a run already
      // under way must not fail because history bookkeeping did.
      console.warn('[chat] failed to bind conversation context', err);
    });
  }

  function reduce(event: ChatEvent, assistant: ChatMessage): void {
    switch (event.type) {
      case EventType.RUN_STARTED:
        taskId.value = event.runId;
        assistant.taskId = event.runId;
        bindContext(event.threadId);
        break;

      case EventType.TEXT_MESSAGE_START:
        assistant.status = 'streaming';
        break;

      case EventType.TEXT_MESSAGE_CONTENT:
        appendText(assistant, event.delta);
        break;

      case EventType.TEXT_MESSAGE_END:
        break;

      case EventType.REASONING_MESSAGE_START:
        upsertStep(assistant, {
          id: `thought:${event.messageId}:${assistant.steps?.length ?? 0}`,
          kind: 'thought',
          content: '',
          timestamp: new Date(),
        });
        break;

      case EventType.REASONING_MESSAGE_CONTENT: {
        const step = assistant.steps?.filter((s) => s.kind === 'thought').pop();
        if (step) step.content += event.delta;
        break;
      }

      case EventType.REASONING_MESSAGE_END:
        break;

      case EventType.TOOL_CALL_START:
        upsertStep(assistant, {
          id: `tool_call:${event.toolCallId}`,
          kind: 'tool_call',
          content: '',
          toolName: event.toolCallName,
          toolCallId: event.toolCallId,
          argumentsJson: '',
          timestamp: new Date(),
        });
        break;

      case EventType.TOOL_CALL_ARGS: {
        const step = assistant.steps?.find((s) => s.id === `tool_call:${event.toolCallId}`);
        if (step) step.argumentsJson = (step.argumentsJson ?? '') + event.delta;
        break;
      }

      case EventType.TOOL_CALL_END:
        break;

      case EventType.TOOL_CALL_RESULT:
        upsertStep(assistant, {
          id: `tool_result:${event.toolCallId}`,
          kind: 'tool_result',
          content: event.content,
          toolCallId: event.toolCallId,
          output: event.content,
          timestamp: new Date(),
        });
        break;

      case EventType.STEP_STARTED:
      case EventType.STEP_FINISHED:
        // Generic progress markers with no per-message meaning. The typed steps
        // above carry everything the UI renders.
        break;

      case EventType.RUN_ERROR:
        assistant.status = 'error';
        assistant.error = event.message;
        error.value = event.message;
        status.value = 'error';
        break;

      case EventType.RUN_FINISHED: {
        if (isCancelledRun(event)) {
          assistant.status = 'cancelled';
          status.value = 'idle';
          break;
        }

        const interrupts = runInterrupts(event);
        if (interrupts.length > 0) {
          const it = interrupts[0];
          const meta = (it.metadata ?? {}) as Record<string, unknown>;
          assistant.status = 'awaiting_input';
          assistant.approval = {
            kind:
              it.reason === INTERRUPT_REASON_TOOL_EXECUTION ? 'tool_execution' : 'tool_approval',
            toolName: String(meta.toolName ?? it.message ?? ''),
            toolCallId: it.toolCallId ?? it.id,
            argumentsJson: String(meta.argumentsJson ?? ''),
            taskId: String(meta.taskId ?? taskId.value ?? ''),
          };
          status.value = 'awaiting_input';
          break;
        }

        const result = (event.result ?? {}) as Record<string, unknown>;
        const usage = result.usage as ChatMessage['usage'] | undefined;
        if (usage) assistant.usage = usage;
        assistant.status = 'complete';
        status.value = 'idle';
        persist(assistant);
        break;
      }
    }
  }

  /** Consumes a transport stream into the given assistant message. */
  async function consume(stream: AsyncIterable<ChatEvent>, assistant: ChatMessage): Promise<void> {
    try {
      for await (const event of stream) reduce(event, assistant);
    } catch (err) {
      const message = toUserMessage(err, 'The response was interrupted. Try sending your message again.');
      assistant.status = 'error';
      assistant.error = message;
      error.value = message;
      status.value = 'error';
      return;
    }
    // A stream that ends without a terminal event still has to settle, or the UI
    // spins forever on a message that will never change.
    if (assistant.status === 'streaming') {
      assistant.status = 'complete';
      status.value = 'idle';
      persist(assistant);
    }
  }

  async function send(content: ContentPart[], input: { model?: string } = {}): Promise<void> {
    if (status.value === 'streaming' || content.length === 0) return;

    const user: ChatMessage = {
      id: newId(),
      role: 'user',
      content,
      status: 'complete',
      timestamp: new Date(),
    };
    const assistant: ChatMessage = {
      id: newId(),
      role: 'assistant',
      content: [],
      status: 'streaming',
      model: input.model,
      timestamp: new Date(),
    };

    const history = [...messages.value];
    messages.value.push(user, assistant);
    persist(user);

    // Fold into the *reactive* message, not the object that was pushed. `push`
    // stores the raw object; Vue renders the proxy it hands back on read, so
    // mutating the raw reference updates the data while the DOM never hears
    // about it — the reply would arrive complete in state and never appear.
    const live = messages.value[messages.value.length - 1];

    status.value = 'streaming';
    error.value = null;
    controller = new AbortController();

    try {
      const stream = opts.transport.send(
        {
          content,
          conversationId: opts.conversationId?.value,
          contextId: contextId.value,
          model: input.model,
          history,
        },
        controller.signal,
      );
      await consume(stream, live);
    } finally {
      controller = null;
    }
  }

  async function approve(toolCallId: string, approved: boolean): Promise<void> {
    const message = messages.value.find((m) => m.approval?.toolCallId === toolCallId);
    const approval = message?.approval;
    if (!message || !approval || !opts.transport.respondToApproval) return;

    message.approval = undefined;
    message.status = 'streaming';
    status.value = 'streaming';

    await opts.transport.respondToApproval({
      taskId: approval.taskId,
      toolCallId,
      approved,
      toolName: approval.toolName,
    });

    // The resumed run's events arrive on the stream this message is already
    // being folded from, so there is nothing to re-subscribe to. A rejected call
    // still ends the turn.
    if (!approved) {
      message.status = 'complete';
      status.value = 'idle';
    }
  }

  async function submitToolResult(toolCallId: string, output: string): Promise<void> {
    const message = messages.value.find((m) => m.approval?.toolCallId === toolCallId);
    const approval = message?.approval;
    if (!message || !approval || !opts.transport.submitToolResult) return;

    message.approval = undefined;
    message.status = 'streaming';
    status.value = 'streaming';

    await opts.transport.submitToolResult({
      taskId: approval.taskId,
      toolCallId,
      output,
      toolName: approval.toolName,
    });
  }

  async function cancel(): Promise<void> {
    controller?.abort();
    controller = null;
    if (opts.transport.cancel && taskId.value) {
      await opts.transport.cancel(taskId.value);
    }
    const streaming = messages.value.find((m) => m.status === 'streaming');
    if (streaming) streaming.status = 'cancelled';
    status.value = 'idle';
  }

  async function loadHistory(conversationId: string): Promise<void> {
    const [conversation, page] = await Promise.all([
      conversationsApi.get(conversationId),
      conversationsApi.listMessages(conversationId),
    ]);
    messages.value = page.messages;
    contextId.value = conversation.context_id;
    status.value = 'idle';
    error.value = null;
  }

  function reset(): void {
    controller?.abort();
    controller = null;
    messages.value = [];
    contextId.value = undefined;
    taskId.value = undefined;
    error.value = null;
    status.value = 'idle';
  }

  return {
    // `computed`, not `readonly`: both make the list read-only to callers, but
    // `readonly` also rewrites the element type as DeepReadonly, which then has
    // to be threaded through every component prop or cast away at the seam.
    messages: computed(() => messages.value),
    status: readonly(status),
    error: readonly(error),
    contextId: readonly(contextId),
    taskId: readonly(taskId),
    capabilities: opts.transport.capabilities,
    send,
    approve,
    submitToolResult,
    cancel,
    loadHistory,
    reset,
  };
}
